import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
// src/features/verify/VerifyPrescription.jsx
import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import TopBar from "@/sot/primitives/TopBar";
import supabase from "@/services/supabaseClient";
import { claimPharmacyPrescription } from "@/lib/payments/api.ts";
import { readPharmacyProfile } from "@/services/pharmacyLaneService.js";
import {
  selectDoctorDisplayName,
  selectDoctorLicenseNumber,
  selectDoctorPrescriptionName,
} from "@/utils/prescriptionDoctorIdentity.js";
import { buildPrescriptionDosageLabel } from "@/utils/prescriptionSchedule.js";
import {
  isPrescriptionUuid,
  readPrescriptionVerifyContext,
  shouldRestorePrescriptionVerifyContext,
} from "@/utils/prescriptionVerification.js";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

// ✅ Same assets used on prescription screens
const SIG_PLACEHOLDER = "/icons/appointments/e-prescription/Signature.svg";
const DR_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";

const abs = (p) =>
  /^(?:https?:|data:|blob:)/i.test(String(p || "").trim())
    ? String(p || "").trim()
    : `${window.location.origin}${p}`;

function safeDate(d) {
  if (d instanceof Date) {
    return !Number.isNaN(d.getTime()) ? d : null;
  }
  if (d === null || d === undefined || d === "") return null;
  const parsed = new Date(d);
  return !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function fmtDate(d) {
  const value = safeDate(d);
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(value);
  } catch {
    return "—";
  }
}

function fmtDateTime(d) {
  const value = safeDate(d);
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(value);
  } catch {
    return "—";
  }
}

function parseMoneyInputToCents(value) {
  const normalized = String(value || "")
    .replace(/[^\d.,-]/g, "")
    .replace(/,/g, ".")
    .trim();
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(Math.round(parsed * 100), 0);
}

function centsToInputValue(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return (parsed / 100).toFixed(2);
}

function resolveCurrencyCode(country = "") {
  const normalized = String(country || "").trim().toLowerCase();
  if (normalized.includes("botswana")) return "BWP";
  if (normalized.includes("south africa")) return "ZAR";
  if (normalized.includes("switzerland")) return "CHF";
  return "CHF";
}

function formatMinorCurrency(value, currency = "CHF", locale = "en-CH") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.max(Number(value || 0), 0) / 100);
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center h-7 px-3 rounded-full text-body-small bg-[rgba(var(--clr-on-surface),0.08)] text-on-surface">
      {children}
    </span>
  );
}

function Row({ label, value, withDivider = true }) {
  return (
    <div className="py-2">
      <div className="flex items-start justify-between gap-6">
        <div className="text-label-medium text-on-surface-var">{label}</div>
        <div className="text-body-medium text-on-surface text-right break-words">{value}</div>
      </div>
      {withDivider ? <div className="mt-2 h-px w-full bg-[rgba(var(--clr-outline-variant),0.55)]" /> : null}
    </div>
  );
}

export default function VerifyPrescription() {
  const { t, languageCode } = useLanguage();
  const nav = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const shouldRestoreContext = React.useMemo(() => shouldRestorePrescriptionVerifyContext(), []);
  const verifyContext = React.useMemo(
    () =>
      location.state ||
      (shouldRestoreContext ? readPrescriptionVerifyContext(id) : null) ||
      null,
    [id, location.state, shouldRestoreContext]
  );
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");
      setData(null);

      try {
        if (!id || !isPrescriptionUuid(id)) {
          throw new Error(t("verify.prescription.errors.invalidCode", "Invalid prescription code."));
        }

        // Public verification payload is intentionally anonymous-accessible.
        const { data: rpcData, error: rpcErr } = await supabase.rpc("verify_prescription_public", {
          rx_id: id,
        });

        if (rpcErr) throw rpcErr;

        const normalized = Array.isArray(rpcData) ? rpcData[0] || null : rpcData || null;
        const nextData = normalized;
        if (!nextData) {
          throw new Error(
            t(
              "verify.prescription.errors.unavailable",
              "Prescription verification data is unavailable.",
            ),
          );
        }

        if (!alive) return;
        setData(nextData);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || t("verify.prescription.errors.failed", "Verification failed."));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, t]);

  const status = data?.status || "—";
  const isSigned = Boolean(data?.is_signed ?? data?.isSigned);
  const sourceLabel =
    verifyContext?.from === "pharmacy-scan"
      ? verifyContext?.entryMethod === "manual"
        ? t("verify.prescription.source.manualPharmacy", "Entered manually at pharmacy")
        : t("verify.prescription.source.scannedPharmacy", "Scanned at pharmacy")
      : t("verify.prescription.source.public", "Public verification view");
  const isPharmacyFlow =
    verifyContext?.from === "pharmacy-scan" || verifyContext?.actorRole === "pharmacy";
  const actionLabel =
    verifyContext?.from === "pharmacy-scan"
      ? t("verify.prescription.actions.scanAnother", "Scan another prescription")
      : String(verifyContext?.returnTo || "").trim()
        ? t("verify.prescription.actions.returnToPrescription", "Return to prescription")
        : "";
  const pharmacyProfile = React.useMemo(
    () => (isPharmacyFlow ? readPharmacyProfile() || {} : {}),
    [isPharmacyFlow],
  );
  const quoteCurrency = React.useMemo(
    () =>
      resolveCurrencyCode(
        pharmacyProfile?.country ||
          pharmacyProfile?.countryName ||
          pharmacyProfile?.country_code ||
          "",
      ),
    [pharmacyProfile],
  );
  const modeOptions = React.useMemo(() => {
    const pickupEnabled = pharmacyProfile?.pickupEnabled !== false;
    const deliveryEnabled = Boolean(pharmacyProfile?.deliveryEnabled);
    const options = [];
    if (pickupEnabled) {
      options.push({ value: "pickup", label: t("pharmacy.common.pickup", "Pickup") });
    }
    if (deliveryEnabled) {
      options.push({ value: "delivery", label: t("pharmacy.common.delivery", "Delivery") });
    }
    if (!options.length) {
      options.push({ value: "pickup", label: t("pharmacy.common.pickup", "Pickup") });
      options.push({ value: "delivery", label: t("pharmacy.common.delivery", "Delivery") });
    }
    return options;
  }, [pharmacyProfile, t]);
  const defaultDeliveryFeeInput = React.useMemo(() => {
    const parsed = Number(pharmacyProfile?.deliveryBaseFee || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return "";
    return parsed.toFixed(2);
  }, [pharmacyProfile]);
  const [quoteForm, setQuoteForm] = React.useState(() => ({
    medicineSubtotal: "",
    deliveryFee: defaultDeliveryFeeInput,
    fulfillmentMode:
      modeOptions.find((option) => option.value === "pickup")?.value ||
      modeOptions[0]?.value ||
      "pickup",
  }));
  const [claimState, setClaimState] = React.useState({
    loading: false,
    error: "",
    success: "",
  });

  React.useEffect(() => {
    setQuoteForm((current) => ({
      medicineSubtotal: current.medicineSubtotal,
      deliveryFee: current.deliveryFee || defaultDeliveryFeeInput,
      fulfillmentMode:
        modeOptions.some((option) => option.value === current.fulfillmentMode)
          ? current.fulfillmentMode
          : modeOptions.find((option) => option.value === "pickup")?.value ||
            modeOptions[0]?.value ||
            "pickup",
    }));
  }, [defaultDeliveryFeeInput, modeOptions]);

  // signature_image: in your model it’s a snapshot path/url.
  // For public view we simply render if present.
  const sigSrc = data?.signature_image || null;
  const prescriptionView = React.useMemo(() => {
    if (!data) return null;

    const doctorPrescriptionName =
      data?.doctorPrescriptionName ||
      selectDoctorPrescriptionName(data) ||
      selectDoctorDisplayName(data) ||
      t("verify.prescription.defaults.prescriber", "Prescriber");
    const doctorName =
      selectDoctorDisplayName(data) ||
      doctorPrescriptionName ||
      t("verify.prescription.defaults.prescriber", "Prescriber");
    const doctorReg = selectDoctorLicenseNumber(data);

    return {
      rxNo: prescriptionIdentifier(data || {id}),
      medicineName: data?.medicineName || t("verify.prescription.defaults.medication", "Medication"),
      form: data?.form || data?.strengthLabel || "",
      ingredients: data?.ingredients || data?.composition || "",
      frequencyLabel: data?.frequencyLabel || "—",
      dosageLabel:
        buildPrescriptionDosageLabel({
          dosageLabel: data?.dosageLabel || data?.dosage || "",
          strengthValue: data?.strengthValue || "",
          strengthUnit: data?.strengthUnit || "",
          strengthLabel: data?.strengthLabel || "",
          dosesPerTime: data?.dosesPerTime || "",
          doseUnitLabel: data?.doseUnitLabel || "",
        }) || "—",
      startsOn: data?.startsOn || data?.issued_at || null,
      endsOn: data?.endsOn || data?.expires_at || null,
      issuedAt: data?.issued_at || null,
      signedAt: data?.signed_at || null,
      expiresAt: data?.expires_at || null,
      digitalSeal: data?.digital_seal || "—",
      issuedVia: data?.issuedVia || "AfriMed™",
      patient: {
        name:
          data?.patientDisplay ||
          data?.patientMaskedName ||
          t("verify.prescription.defaults.patientMasked", "Patient identity masked"),
        yearOfBirth: data?.patientYearOfBirth || "",
      },
      doctor: {
        name: doctorName,
        prescriptionName: doctorPrescriptionName,
        regNo: doctorReg,
        signatureSrc: sigSrc || SIG_PLACEHOLDER,
        facility: {
          name: data?.facilityName || t("verify.prescription.defaults.facility", "Facility"),
        },
      },
    };
  }, [data, id, sigSrc, t]);

  function handleBack() {
    const returnTo = String(verifyContext?.returnTo || "").trim();
    if (returnTo) {
      nav(returnTo, {
        state: verifyContext?.returnState || null,
      });
      return;
    }

    nav(-1);
  }

  async function handlePreparePatientPayment() {
    setClaimState({ loading: true, error: "", success: "" });

    try {
      const medicineSubtotalCents = parseMoneyInputToCents(quoteForm.medicineSubtotal);
      const deliveryFeeCents =
        quoteForm.fulfillmentMode === "delivery"
          ? parseMoneyInputToCents(quoteForm.deliveryFee)
          : 0;

      if (!medicineSubtotalCents) {
        throw new Error(
          t(
            "verify.prescription.payment.errors.missingSubtotal",
            "Medicine subtotal is required before you can send the patient to payment.",
          ),
        );
      }

      const result = await claimPharmacyPrescription({
        prescription_id: String(id || "").trim(),
        medicine_subtotal_cents: medicineSubtotalCents,
        delivery_fee_cents: deliveryFeeCents,
        fulfillment_mode: quoteForm.fulfillmentMode,
        currency: quoteCurrency,
        meta: {
          quoted_from: "pharmacy_verify_screen",
          medicine_name: prescriptionView?.medicineName || data?.medicineName || "",
          patient_name: prescriptionView?.patient?.name || data?.patientDisplay || "Patient identity masked",
          doctor_name:
            prescriptionView?.doctor?.prescriptionName ||
            prescriptionView?.doctor?.name ||
            data?.doctorName ||
            "",
          prescription_form: prescriptionView?.form || data?.form || "",
          external_rx_id: prescriptionView?.rxNo || data?.rxNo || "",
        },
      });

      setClaimState({
        loading: false,
        error: "",
        success: t(
          "verify.prescription.payment.success",
          "{pharmacy} is now ready. The patient can open this prescription and pay {total}.",
          {
            pharmacy:
              result?.pharmacy_name ||
              t("verify.prescription.payment.defaults.quote", "Pharmacy quote"),
            total: formatMinorCurrency(
              medicineSubtotalCents + deliveryFeeCents,
              String(result?.currency || quoteCurrency || "CHF").toUpperCase(),
              languageCode,
            ),
          },
        ),
      });
    } catch (claimError) {
      setClaimState({
        loading: false,
        error:
          claimError?.message ||
          t(
            "verify.prescription.payment.errors.prepareFailed",
            "Could not prepare patient payment right now.",
          ),
        success: "",
      });
    }
  }

  const statusPrefix = t("verify.prescription.status", "Status");
  const signedLabel = t("verify.prescription.signed", "Signed");
  const notSignedLabel = t("verify.prescription.notSigned", "Not signed");
  const publicRouteCopy = t(
    "verify.prescription.publicRouteCopy",
    "{source}. Authenticity-only public verification. Full identity details require authenticated AfriMed access or a regulated pharmacy workflow.",
    { source: sourceLabel },
  );
  const patientLabel = t("verify.prescription.patient", "Patient");
  const yearOfBirthLabel = t("verify.prescription.yearOfBirth", "Year of birth");
  const regLabel = t("verify.prescription.reg", "Reg");
  const facilityLabel = t("verify.prescription.defaults.facility", "Facility");
  const ePrescriptionLabel = t("verify.prescription.title", "E-Prescription");
  const prescriptionNumberLabel = t("verify.prescription.number", "Prescription No");
  const issuedLabel = t("verify.prescription.issued", "Issued");
  const scheduleLabel = t("verify.prescription.schedule", "Schedule");
  const frequencyLabel = t("verify.prescription.frequency", "Frequency");
  const dosageLabel = t("verify.prescription.dosage", "Dosage");
  const startsLabel = t("verify.prescription.starts", "Starts");
  const endsLabel = t("verify.prescription.ends", "Ends");
  const verificationLabel = t("verify.prescription.verification", "Verification");
  const expiresLabel = t("verify.prescription.expires", "Expires");
  const digitalSealLabel = t("verify.prescription.digitalSeal", "Digital seal");
  const prescriberVerificationLabel = t(
    "verify.prescription.prescriberVerification",
    "Prescriber verification",
  );
  const signatureAlt = t("verify.prescription.signatureAlt", "Prescriber signature");
  const verifiedViaCopy = t(
    "verify.prescription.verifiedVia",
    "Verified via {issuer}. The prescriber remains responsible for clinical decisions.",
    {
      issuer: prescriptionView?.issuedVia || "AfriMed™",
    },
  );

  return (
    <div className="pt-4 pb-10 md:pb-16">
      <div className="no-print">
        <TopBar
          title={t("verify.prescription.pageTitle", "Verify prescription")}
          onBack={handleBack}
          trailing={
            !loading && !err && prescriptionView ? (
              <Button variant="tonal" onClick={() => nav(`/appointments/prescriptions/${id}`)}>
                {t("prescriptions.common.openDocument", "Open E-Prescription PDF")}
              </Button>
            ) : null
          }
        />
      </div>

      <div className="no-print mt-6">
        <Card className="overflow-hidden">
          <div className="p-4 md:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Pill>{statusPrefix}: {status}</Pill>
              <Pill>{isSigned ? signedLabel : notSignedLabel}</Pill>
            </div>

            <div className="mt-4 text-body-small text-on-surface-var">
              {publicRouteCopy}
            </div>

            <div className="mt-4">
              {loading ? (
                <div className="text-body-medium text-on-surface-var animate-pulse">
                  {t("verify.prescription.checking", "Checking…")}
                </div>
              ) : err ? (
                <div className="text-body-medium text-on-surface-var">{err}</div>
              ) : (
                <>
                  <div className="mt-6">
                    <div
                      className="mx-auto w-full max-w-[720px]"
                      style={{ fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial" }}
                    >
                        <div className="flex items-start justify-between gap-6">
                          <div className="min-w-0">
                            <div className="text-title-medium text-on-surface">{patientLabel}</div>
                            <div className="mt-1 text-body-medium text-on-surface">
                              {prescriptionView?.patient?.name || "—"}
                            </div>
                            {prescriptionView?.patient?.yearOfBirth ? (
                              <div className="mt-1 text-body-small text-on-surface-var">
                                {yearOfBirthLabel}: <span className="text-on-surface">{prescriptionView.patient.yearOfBirth}</span>
                              </div>
                            ) : null}
                          </div>

                          <div className="min-w-0 text-left md:text-right">
                            <div className="text-title-medium text-on-surface">
                              {prescriptionView?.doctor?.facility?.name || facilityLabel}
                            </div>
                            <div className="mt-1 text-body-medium text-on-surface">
                              {prescriptionView?.doctor?.prescriptionName || prescriptionView?.doctor?.name || "—"}
                            </div>
                            <div className="mt-1 text-body-small text-on-surface-var">
                              {regLabel}: <span className="text-on-surface">{prescriptionView?.doctor?.regNo || "—"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="text-title-large text-on-surface">{ePrescriptionLabel}</div>
                          <div className="mt-1 text-body-medium text-on-surface-var">
                            {prescriptionNumberLabel}: <span className="text-on-surface">{prescriptionView?.rxNo || "—"}</span>
                            {" · "}
                            {issuedLabel}: <span className="text-on-surface">{fmtDate(prescriptionView?.issuedAt)}</span>
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="text-title-large text-on-surface">
                            {prescriptionView?.medicineName ||
                              t("verify.prescription.defaults.medication", "Medication")}
                          </div>
                          {prescriptionView?.form ? (
                            <div className="mt-1 text-body-medium text-on-surface">{prescriptionView.form}</div>
                          ) : null}
                          {prescriptionView?.ingredients ? (
                            <div className="mt-1 text-body-medium text-on-surface-var">
                              {prescriptionView.ingredients}
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-6">
                          <div className="text-label-large text-on-surface-var">{scheduleLabel}</div>
                          <div className="mt-3">
                            <Row label={frequencyLabel} value={prescriptionView?.frequencyLabel || "—"} />
                            <Row label={dosageLabel} value={prescriptionView?.dosageLabel || "—"} />
                            <Row label={startsLabel} value={fmtDate(prescriptionView?.startsOn)} />
                            <Row label={endsLabel} value={fmtDate(prescriptionView?.endsOn)} withDivider={false} />
                          </div>
                        </div>

                        <div className="mt-6">
                          <div className="text-label-large text-on-surface-var">{verificationLabel}</div>
                          <div className="mt-3">
                            <Row label={issuedLabel} value={fmtDateTime(prescriptionView?.issuedAt)} />
                            <Row label={signedLabel} value={fmtDateTime(prescriptionView?.signedAt)} />
                            <Row label={expiresLabel} value={fmtDateTime(prescriptionView?.expiresAt)} />
                            <Row
                              label={digitalSealLabel}
                              value={prescriptionView?.digitalSeal || "—"}
                              withDivider={false}
                            />
                          </div>
                        </div>

                        <div className="mt-6 text-body-small text-on-surface-var">
                          {prescriberVerificationLabel}
                        </div>

                        <SignatureStampLine
                          sigSrc={prescriptionView?.doctor?.signatureSrc || SIG_PLACEHOLDER}
                          sigAlt={signatureAlt}
                          sigClassName="h-[68px] w-[160px]"
                          stampBaseSrc={abs(DR_STAMP_BASE)}
                          doctorName={
                            prescriptionView?.doctor?.prescriptionName ||
                            prescriptionView?.doctor?.name ||
                            t("verify.prescription.defaults.prescriber", "Prescriber")
                          }
                          regNo={prescriptionView?.doctor?.regNo || ""}
                          stampSizePx={72}
                          sigNudgeY={-4}
                          stampNudgeY={-4}
                        />

                        <div className="mt-4 text-body-small text-on-surface-var">
                          {verifiedViaCopy}
                        </div>

                        {isPharmacyFlow ? (
                          <div className="mt-6 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-container-low))] p-4">
                            <div className="text-title-medium text-on-surface">
                              {t("verify.prescription.payment.title", "Prepare patient payment")}
                            </div>
                            <p className="mt-1 text-body-small text-on-surface-var">
                              {t(
                                "verify.prescription.payment.copy",
                                "Quote the medicine subtotal and delivery terms now so the patient can pay the dispensing branch directly without a second chase.",
                              )}
                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                              <label className="grid gap-2">
                                <span className="text-label-large text-on-surface-var">
                                  {t("verify.prescription.payment.medicineSubtotal", "Medicine subtotal")}
                                </span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={quoteForm.medicineSubtotal}
                                  onChange={(event) =>
                                    setQuoteForm((current) => ({
                                      ...current,
                                      medicineSubtotal: event.target.value,
                                    }))
                                  }
                                  placeholder="0.00"
                                  className="h-12 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.65)] bg-[rgb(var(--clr-surface))] px-4 text-body-large text-on-surface outline-none focus:border-[rgb(var(--afrimed-primary))]"
                                />
                              </label>

                              <div className="grid gap-2">
                                <span className="text-label-large text-on-surface-var">
                                  {t("verify.prescription.payment.fulfillmentMode", "Fulfilment mode")}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {modeOptions.map((option) => {
                                    const isActive = quoteForm.fulfillmentMode === option.value;
                                    return (
                                      <button
                                        key={option.value}
                                        type="button"
                                        onClick={() =>
                                          setQuoteForm((current) => ({
                                            ...current,
                                            fulfillmentMode: option.value,
                                          }))
                                        }
                                        className={[
                                          "h-11 rounded-full px-4 text-label-large transition-colors",
                                          isActive
                                            ? "bg-[rgba(var(--afrimed-primary),0.14)] text-[rgb(var(--afrimed-primary))] shadow-[0_0_0_1px_rgba(var(--afrimed-primary),0.35)]"
                                            : "bg-[rgba(var(--clr-on-surface),0.06)] text-on-surface-var",
                                        ].join(" ")}
                                      >
                                        {option.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              <label className="grid gap-2">
                                <span className="text-label-large text-on-surface-var">
                                  {t("verify.prescription.payment.deliveryFee", "Delivery fee")}
                                </span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={quoteForm.deliveryFee}
                                  disabled={quoteForm.fulfillmentMode !== "delivery"}
                                  onChange={(event) =>
                                    setQuoteForm((current) => ({
                                      ...current,
                                      deliveryFee: event.target.value,
                                    }))
                                  }
                                  placeholder="0.00"
                                  className="h-12 rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.65)] bg-[rgb(var(--clr-surface))] px-4 text-body-large text-on-surface outline-none focus:border-[rgb(var(--afrimed-primary))] disabled:opacity-50"
                                />
                              </label>

                              <div className="grid gap-2">
                                <span className="text-label-large text-on-surface-var">
                                  {t("verify.prescription.payment.patientTotal", "Patient total")}
                                </span>
                                <div className="flex h-12 items-center rounded-[12px] bg-[rgba(var(--afrimed-secondary),0.08)] px-4 text-title-medium text-on-surface">
                                  {formatMinorCurrency(
                                    parseMoneyInputToCents(quoteForm.medicineSubtotal) +
                                      (quoteForm.fulfillmentMode === "delivery"
                                        ? parseMoneyInputToCents(quoteForm.deliveryFee)
                                        : 0),
                                    quoteCurrency,
                                    languageCode,
                                  )}
                                </div>
                              </div>
                            </div>

                            {claimState.error ? (
                              <div className="mt-4 rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
                                {claimState.error}
                              </div>
                            ) : null}

                            {claimState.success ? (
                              <div className="mt-4 rounded-[12px] bg-[rgba(0,189,196,0.12)] px-4 py-3 text-body-medium text-[#008A8F]">
                                {claimState.success}
                              </div>
                            ) : null}

                            <div className="mt-4 flex flex-wrap gap-3">
                              <Button
                                type="button"
                                onClick={handlePreparePatientPayment}
                                disabled={claimState.loading}
                              >
                                {claimState.loading
                                  ? t("verify.prescription.payment.saving", "Saving quote…")
                                  : t("verify.prescription.payment.send", "Send patient to payment")}
                              </Button>
                              <div className="flex items-center text-body-small text-on-surface-var">
                                {t(
                                  "verify.prescription.payment.auditCopy",
                                  "{currency} quote stored against this prescription for audit and checkout.",
                                  { currency: quoteCurrency },
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                  {actionLabel ? (
                    <div className="mt-5 flex justify-start">
                      <Button variant="tonal" size="sm" onClick={handleBack}>
                        {actionLabel}
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </Card>
      </div>

    </div>
  );
}
